import type { Candle } from "../types";
import { ChartOptions, ChartPoint, DEFAULT_OPTIONS } from "./ChartOptions";
import { computeLayout, Layout } from "./Layout";
import { PriceScale } from "./PriceScale";
import { themeColors } from "./Theme";
import { Viewport } from "./Viewport";
import { candleStep, fracIndexOfTime, timeOfFracIndex } from "./TimeScale";
import type { Frame } from "../renderer/Frame";
import { renderGrid } from "../renderer/GridRenderer";
import { renderLastPrice, renderSeries, renderVolume } from "../renderer/SeriesRenderer";
import { renderOverlays, renderPanels } from "../renderer/IndicatorRenderer";
import { renderStructure } from "../renderer/StructureRenderer";
import { renderDrawings, renderSnapIndicator } from "../renderer/DrawingRenderer";
import { CrosshairState, renderCrosshair } from "../renderer/CrosshairRenderer";
import { attachInteractions } from "../interactions/Interactions";
import { DrawingsController } from "../drawings/DrawingsController";
import type { DrawingObject, ToolId } from "../drawings/types";

export interface ViewState { isLiveFollowing: boolean }

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

// The Felacoo BOS Chart Engine: owns state, scales, renderer pipeline and interactions for one canvas.
export class Chart {
  readonly canvas: HTMLCanvasElement;
  readonly viewport = new Viewport();
  readonly priceScale = new PriceScale();
  readonly drawings: DrawingsController;
  opts: ChartOptions = { ...DEFAULT_OPTIONS };
  symbol = "";
  candles: Candle[] = [];
  crosshair: CrosshairState = { x: -1, y: -1, active: false };
  layout: Layout | null = null;
  private frame: Frame | null = null;
  private raf: number | null = null;
  private liveAnim: number | null = null;
  private lastLive = true;
  private hoverCb: ((c: Candle | null) => void) | null = null;
  private drawingCb: ((d: DrawingObject) => void) | null = null;
  private drawingsChangeCb: ((list: DrawingObject[]) => void) | null = null;
  private selectCb: ((id: string | null) => void) | null = null;
  private editTextCb: ((id: string) => void) | null = null;
  private viewCb: ((s: ViewState) => void) | null = null;
  private detach: () => void;
  private ro: ResizeObserver;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.viewport.reset();
    this.drawings = new DrawingsController({
      list: () => this.opts.drawings,
      symbol: () => this.symbol,
      snapEnabled: () => this.opts.snap,
      onComplete: (d) => this.drawingCb?.(d),
      onChange: (list) => this.drawingsChangeCb?.(list),
      onSelect: (id) => { this.selectCb?.(id); this.requestRender(); },
    });
    this.detach = attachInteractions(this);
    this.ro = new ResizeObserver(() => this.requestRender());
    this.ro.observe(canvas);
    this.requestRender();
  }

  onHover(cb: (c: Candle | null) => void) { this.hoverCb = cb; }
  onDrawingComplete(cb: (d: DrawingObject) => void) { this.drawingCb = cb; }
  onDrawingsChange(cb: (list: DrawingObject[]) => void) { this.drawingsChangeCb = cb; }
  onSelect(cb: (id: string | null) => void) { this.selectCb = cb; }
  onEditText(cb: (id: string) => void) { this.editTextCb = cb; }
  onViewChange(cb: (s: ViewState) => void) { this.viewCb = cb; cb({ isLiveFollowing: this.viewport.isLiveFollowing && !this.priceScale.override }); }

  // New data: when following, the newest bar stays at the live edge; otherwise the viewport is re-anchored by TIME so
  // it stays exactly where the user left it even though indices shifted. A replaced series (symbol/tf) returns to live.
  setData(candles: Candle[]) {
    const old = this.candles, vp = this.viewport;
    if (!vp.isLiveFollowing && old.length && candles.length) {
      const step = candleStep(old, this.opts.tfSeconds);
      const r = vp.range(old.length);
      const tEdge = timeOfFracIndex(old, r.endFrac, step);
      const nstep = candleStep(candles, this.opts.tfSeconds);
      const inside = tEdge >= candles[0].time - nstep * r.count && tEdge <= candles[candles.length - 1].time + nstep * Math.abs(vp.minOffset(r.count));
      if (inside) vp.offset = vp.clampOffset(candles.length - 1 - fracIndexOfTime(candles, tEdge, nstep), candles.length, r.count);
      else vp.goLive(candles.length);
    }
    this.candles = candles;
    this.requestRender();
  }
  setOptions(patch: Partial<ChartOptions>) { this.opts = { ...this.opts, ...patch }; this.requestRender(); }
  setSymbol(symbol: string) { if (symbol !== this.symbol) { this.symbol = symbol; this.viewport.goLive(this.candles.length); } }
  setTool(tool: ToolId, meta?: Record<string, unknown>) { this.opts.activeTool = tool; this.drawings.pending = null; this.drawings.quick = false; this.drawings.toolMeta = meta ?? null; this.requestRender(); }
  selectDrawing(id: string | null) { this.drawings.select(id); this.requestRender(); }
  cancelDrawing() { this.drawings.cancel(); this.requestRender(); }
  finishPending() { this.drawings.finish(); this.requestRender(); }
  editText(id: string) { this.editTextCb?.(id); }

  emitHover(c: Candle | null) { this.hoverCb?.(c); }

  resetView() {
    this.viewport.reset();
    this.priceScale.override = null;
    this.requestRender();
  }

  // Smoothly animate the viewport back to the newest candle + the normal future margin, then resume following (auto price scale restored).
  goToLive(duration = 420) {
    if (this.liveAnim != null) cancelAnimationFrame(this.liveAnim);
    this.priceScale.override = null;
    const vp = this.viewport, N = this.candles.length;
    const from = vp.offset, to = vp.liveOffset(vp.clampCount(vp.count, N));
    const t0 = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / duration);
      vp.offset = from + (to - from) * easeOut(k);
      if (k < 1) { this.liveAnim = requestAnimationFrame(tick); this.requestRender(); return; }
      this.liveAnim = null;
      vp.goLive(N);
      this.requestRender();
    };
    this.liveAnim = requestAnimationFrame(tick);
  }

  // Convert canvas pixel → (index, price) in the current frame.
  pointAt(x: number, y: number): ChartPoint | null {
    const f = this.frame;
    if (!f) return null;
    return { index: f.indexAt(x), price: f.priceAt(y) };
  }

  get currentFrame(): Frame | null { return this.frame; }

  requestRender() {
    if (this.raf != null) return;
    this.raf = requestAnimationFrame(() => { this.raf = null; this.render(); });
  }

  private buildFrame(ctx: CanvasRenderingContext2D, w: number, h: number): Frame | null {
    const cs = this.candles;
    const N = cs.length;
    if (!N) return null;
    const L = computeLayout(w, h, this.opts.panels);
    const { count, start, end, startFrac, endFrac } = this.viewport.range(N);
    const pr = this.priceScale.compute(cs, start, Math.max(start, end));
    if (!pr) return null;
    const range = pr.max - pr.min || 1;
    // log scale = transform the whole price axis (candles, drawings, structure, overlays all go through y()/priceAt())
    const useLog = this.opts.scaleMode === "log" && pr.min > 0;
    const T = useLog ? (p: number) => Math.log(Math.max(p, 1e-9)) : (p: number) => p;
    const Tinv = useLog ? (v: number) => Math.exp(v) : (v: number) => v;
    const tRange = T(pr.max) - T(pr.min) || 1;
    const candleW = L.plotW / count;
    const step = candleStep(cs, this.opts.tfSeconds);
    const x = (i: number) => L.plotLeft + (i - startFrac + 0.5) * candleW;
    const fracIndexAt = (px: number) => startFrac + (px - L.plotLeft) / candleW - 0.5;
    return {
      ctx, L, candles: cs, start, end, startFrac, endFrac, count, candleW,
      bodyW: candleW >= 4 ? Math.max(3, Math.min(candleW * 0.7, 22)) : Math.max(1, candleW * 0.75), // body ≈ 70% of bar spacing, bounded
      minP: pr.min, maxP: pr.max,
      x,
      y: (p) => L.plotTop + ((T(pr.max) - T(p)) / tRange) * L.priceH,
      priceAt: (y) => Tinv(T(pr.max) - ((y - L.plotTop) / L.priceH) * tRange),
      indexAt: (px) => Math.round(fracIndexAt(px)),
      fracIndexAt,
      xt: (t) => x(fracIndexOfTime(cs, t, step)),
      timeAt: (px) => timeOfFracIndex(cs, fracIndexAt(px), step),
      step,
      opts: this.opts,
      tc: themeColors(this.opts.theme),
    };
  }

  render() {
    const canvas = this.canvas;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width, h = rect.height;
    if (!w || !h) return;
    if (canvas.width !== Math.round(w * dpr)) canvas.width = Math.round(w * dpr);
    if (canvas.height !== Math.round(h * dpr)) canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const f = this.buildFrame(ctx, w, h);
    this.frame = f;
    const liveNow = this.viewport.isLiveFollowing && !this.priceScale.override; // "live" = following newest candle AND auto price scale
    if (this.lastLive !== liveNow) { this.lastLive = liveNow; this.viewCb?.({ isLiveFollowing: liveNow }); }
    if (!f) return;
    this.layout = f.L;

    renderGrid(f);
    if (this.opts.showVolume) renderVolume(f);
    renderStructure(f);
    renderSeries(f);
    renderOverlays(f);
    renderDrawings(f, this.drawings.renderList(), this.drawings.selectedId, false, this.crosshair.active ? { x: this.crosshair.x, y: this.crosshair.y } : null);
    const pending = this.drawings.pendingObject();
    if (pending) renderDrawings(f, [pending], null, true);
    renderLastPrice(f);
    renderPanels(f);
    if (this.opts.activeTool !== "select") renderCrosshair(f, this.crosshair, this.opts.activeTool);
    renderSnapIndicator(f, this.drawings.snap);
  }

  destroy() {
    if (this.raf != null) cancelAnimationFrame(this.raf);
    if (this.liveAnim != null) cancelAnimationFrame(this.liveAnim);
    this.detach();
    this.ro.disconnect();
  }
}
